import React from "react";
import { Loader2, ShieldCheck, BadgeCheck, Phone, Mail, Building2, Star, Search, PackageOpen, Inbox, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const cn = (...c) => c.filter(Boolean).join(" ");

export function Spinner({ className }) {
  return <Loader2 className={cn("h-5 w-5 animate-spin text-primary", className)} />;
}

export function PageLoading() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#0b0b0c]" data-testid="page-loading">
      <Loader2 className="h-7 w-7 animate-spin text-[#0A7A50]" />
      <p className="text-sm text-neutral-400">Loading…</p>
    </div>
  );
}

export function ErrorState({ title = "Something went wrong", subtitle = "Please try again.", onRetry, testid = "error-state" }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0b0b0c] px-6 text-center" data-testid={testid}>
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10"><AlertTriangle className="h-7 w-7 text-red-500" /></div>
      <div><h2 className="font-display text-xl font-bold text-neutral-100">{title}</h2><p className="mt-1 text-sm text-neutral-400">{subtitle}</p></div>
      <div className="flex gap-2">
        {onRetry && <button onClick={onRetry} data-testid="error-retry" className="rounded-xl bg-[#0A7A50] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0A7A50]/90">Try Again</button>}
        <Link to="/app" data-testid="error-home" className="rounded-xl border border-neutral-700 px-4 py-2 text-sm font-semibold text-neutral-200 transition-colors hover:bg-neutral-800">Back to Home</Link>
      </div>
    </div>
  );
}

const BADGES = {
  email_verified: { icon: Mail, label: "Email Verified", cls: "bg-sky-500/10 text-sky-700 border-sky-500/30 dark:text-sky-400" },
  phone_verified: { icon: Phone, label: "Phone Verified", cls: "bg-sky-500/10 text-sky-700 border-sky-500/30 dark:text-sky-400" },
  identity_verified: { icon: ShieldCheck, label: "Identity Verified", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-400" },
  verified: { icon: BadgeCheck, label: "Business Verified", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-400" },
  employer_verified: { icon: Building2, label: "Employer Verified", cls: "bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-400" },
};

export function VerifBadge({ type, size = "sm" }) {
  const b = BADGES[type];
  if (!b) return null;
  const Icon = b.icon;
  return (
    <span data-testid={`badge-${type}`}
      className={cn("inline-flex items-center gap-1 rounded-full border font-semibold", b.cls,
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs")}>
      <Icon className="h-3 w-3" /> {b.label}
    </span>
  );
}

export function Rating({ value = 0, count }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm">
      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
      <span className="font-semibold">{value ? value.toFixed(1) : "New"}</span>
      {count > 0 && <span className="text-muted-foreground">({count})</span>}
    </span>
  );
}

export function DemoTag() {
  return <span data-testid="demo-tag" className="rounded-md bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Demo</span>;
}

export function SafetyBanner({ text }) {
  return (
    <div data-testid="safety-banner" className="flex items-start gap-2 rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <p>{text || "Stay safe. Never send money before viewing items in person or meeting in a safe public place. Never share passwords or verification codes."}</p>
    </div>
  );
}

export function EmptyState({ icon: Icon = PackageOpen, title, subtitle, action, testid }) {
  return (
    <div data-testid={testid || "empty-state"} className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-card/60 px-6 py-14 text-center animate-fade-up">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
        <Icon className="h-7 w-7 text-primary" />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        {subtitle && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export { Search, Inbox };

export function BlockedScreen({ onBack, testid }) {
  return (
    <div data-testid={testid || "blocked-screen"} className="mx-auto flex max-w-md flex-col items-center gap-3 py-20 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10"><ShieldCheck className="h-7 w-7 text-destructive" /></div>
      <h3 className="font-display text-lg font-semibold">Content unavailable</h3>
      <p className="max-w-sm text-sm text-muted-foreground">This content isn't available because of a block between you and this user. Unblock them from Settings → Privacy & Safety to restore access.</p>
      {onBack && <Button variant="outline" onClick={onBack} data-testid="blocked-back">Go back</Button>}
    </div>
  );
}

export function SectionHeader({ title, subtitle, to, cta }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-3">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {to && <Link to={to} className="shrink-0 text-sm font-semibold text-primary hover:underline" data-testid={`section-more-${cta || ""}`}>See all</Link>}
    </div>
  );
}
